import axios from 'axios';
import * as Sentry from '@sentry/react';

// Criar uma instância do axios para chamadas públicas (sem autenticação)
const publicApi = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Public-Access': 'true' // Cabeçalho especial para indicar acesso público
    },
    // Timeout para evitar pendurar em redes instáveis (iOS mobile)
    timeout: 15000,
});

// Interceptor para tratamento de erros
publicApi.interceptors.request.use(
    config => {
        return config;
    },
    error => {
        console.error('Erro na requisição pública:', error);
        return Promise.reject(error);
    }
);

// Interceptor para tratamento de erros básicos (sem redirecionamento para login)
// Helper: aguarda ms
const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

// Helper: loga no Sentry erros de rede com contexto
const reportAxiosError = (error: any) => {
    const cfg = error?.config || {};
    const dataPreview = typeof cfg.data === 'string' && cfg.data.length > 300 ? cfg.data.slice(0, 300) + '…' : cfg.data;
    Sentry.captureException(error, (scope) => {
        scope.setLevel('error');
        scope.setTag('api.client', 'publicApi');
        scope.setExtras({
            url: cfg.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : cfg.url,
            method: cfg.method,
            timeout: cfg.timeout,
            withCredentials: cfg.withCredentials,
            data: dataPreview,
            isOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        });
        return scope;
    });
};

publicApi.interceptors.response.use(
    response => {
        return response;
    },
    async (error) => {
        // Tratar erros sem redirecionar para login
        if (error.response) {
            console.error('Erro na resposta da API pública:', error.response.status, error.response.data);

            // Se receber um erro 401 (não autorizado), retornar um objeto de erro específico
            // mas não redirecionar para a página de login
            if (error.response.status === 401) {
                console.warn('Acesso não autorizado a endpoint público - verifique o middleware no backend');
                // Não fazer redirecionamento para login
            }
        } else if (error.request) {
            console.error('Sem resposta do servidor para API pública');
            reportAxiosError(error);
        } else {
            console.error('Erro na configuração da requisição pública:', error.message);
        }

        // Retry seguro apenas para GET em erros de rede/timeout
        const config = error.config || {};
        const isGet = (config.method || 'get').toLowerCase() === 'get';
        const isNetwork = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
        const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
        config.__retryCount = config.__retryCount || 0;

        if ((isNetwork || isTimeout) && isGet && config.__retryCount < 1) {
            config.__retryCount += 1;
            await wait(500);
            try {
                return await publicApi.request(config);
            } catch (retryErr) {
                reportAxiosError(retryErr);
                return Promise.reject(retryErr);
            }
        }

        return Promise.reject(error);
    }
);

export default publicApi;
